import React, { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useUsers } from '../hooks/useUsers';
import { useAppDate } from '../context/AppContext';
import { MessageReceivePayload, MessageSendPayload, MessageType } from '../datatypes';
import { useUserChatMessages } from '../hooks/useUserChatMessages';
import { Chat } from '../hooks/useChats';



function UserChat() {
  const  { socketRef, userEmail, tool } = useAppDate();
  const { fetchMessages, loading: loadingMessages } = useUserChatMessages();


  const [chat, setChat] = useState<Chat|null>(null);
  const [messages, setMessages] = useState<MessageReceivePayload[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);


  useEffect(() => {
    if(socketRef.current) {
      console.log('Socket connected:', socketRef.current.id);
      socketRef.current.on('message:receive', (payload: MessageReceivePayload) => {
        console.log('Message receive:', payload);
        if(payload.chat.id){
          setChat(payload.chat);
        }
        setMessages((prev) => [...prev?.filter(p => p.content !== payload.content), payload]);
      });
      socketRef.current.on('user:typing', (payload: any) => {
        if (payload.userId !== userEmail) {
          setOtherTyping(payload.isTyping);
        }
      });
    }

    if(userEmail && messages.length === 0) {
      fetchMessages(userEmail).then(data => {
        if(data){          
          setMessages(data.messages);          
          setChat(data.chat);
        }
      });
    }
  }, [socketRef, userEmail]);  
  

  const handleSend = () => {
    if (input.trim() && socketRef.current && userEmail) {      
      const msg: MessageSendPayload = {
        senderId: userEmail,
        content: input,        
        type: MessageType.text,
        tool: tool
      };
      console.log('Sending message:', msg);
      if(chat && chat?.id){
        msg.chatId = chat.id;
      }
      socketRef.current.emit('message:send', msg);
      // setMessages((prev) => [...prev, msg]);
      if(!messages || messages?.length === 0){
        fetchMessages(userEmail);
      }
      setInput('');
      setIsTyping(false);
      socketRef.current.emit('user:typing', { userId: userEmail, isTyping: false });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
    if (!isTyping && socketRef.current) {
      setIsTyping(true);
      // socketRef.current.emit('user:typing', { userId: userEmail, isTyping: true });
    }
    if (isTyping && e.target.value === '' && socketRef.current) {
      setIsTyping(false);
      // socketRef.current.emit('user:typing', { userId: userEmail, isTyping: false });
    }
  };


  return (
    <div className="flex h-screen">
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col h-full">
        <div className="flex-1 overflow-y-auto p-4 max-h-[calc(100vh-80px)] relative" style={{
          backgroundImage: `url('data:image/svg+xml;utf8,<svg width=\"120\" height=\"120\" viewBox=\"0 0 120 120\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\"><rect width=\"120\" height=\"120\" fill=\"%23f3f4f6\"/><path d=\"M0 60 Q30 90 60 60 T120 60\" stroke=\"%239ca3af\" stroke-width=\"1.5\" fill=\"none\" opacity=\"0.18\"/><path d=\"M0 80 Q40 100 80 80 T120 80\" stroke=\"%236b7280\" stroke-width=\"1.2\" fill=\"none\" opacity=\"0.13\"/><ellipse cx=\"40\" cy=\"40\" rx=\"10\" ry=\"18\" fill=\"%239ca3af\" fill-opacity=\"0.08\"/><ellipse cx=\"90\" cy=\"90\" rx=\"14\" ry=\"8\" fill=\"%236b7280\" fill-opacity=\"0.10\"/><!-- Tree icon --><g opacity=\"0.18\"><rect x=\"20\" y=\"90\" width=\"4\" height=\"10\" fill=\"%236b7280\"/><polygon points=\"22,90 16,100 28,100\" fill=\"%239ca3af\"/></g><!-- Car icon --><g opacity=\"0.18\"><rect x=\"70\" y=\"100\" width=\"18\" height=\"6\" rx=\"2\" fill=\"%239ca3af\"/><rect x=\"72\" y=\"98\" width=\"14\" height=\"4\" rx=\"1\" fill=\"%236b7280\"/><circle cx=\"74\" cy=\"108\" r=\"2\" fill=\"%236b7280\"/><circle cx=\"86\" cy=\"108\" r=\"2\" fill=\"%236b7280\"/></g></svg>')`,
          backgroundRepeat: 'repeat',
          backgroundSize: 'auto',
        }}>
          {/* Blur overlay for area outside message area */}
          <div className="pointer-events-none fixed inset-0 z-0" aria-hidden="true">
            <div className="w-full h-full backdrop-blur-md" />
          </div>
          {/* Message area with solid background to remain sharp */}
          <div className="relative z-10 max-w-xl mx-auto space-y-4 rounded-xl bg-white bg-opacity-80 p-2 min-h-[calc(100vh-20px)]">
            {messages?.map((msg, idx) => (
              <div
              key={msg.timestamp?.toString() + idx}
              className={`flex transition-all duration-200 ${msg.senderId === userEmail ? 'justify-end' : 'justify-start'}`}
              >
              <div
                className={`relative group rounded-2xl px-5 py-3 shadow-lg text-sm max-w-xs break-words border transition-all duration-200
                ${msg.senderId === userEmail
                  ? 'bg-gradient-to-br from-blue-400 to-green-600 text-white border-blue-300 shadow-blue-200'
                  : 'bg-white text-gray-800 border-gray-200 shadow-gray-200'}
                hover:scale-105 hover:shadow-xl
                `}
                style={{ animation: 'fadeIn 0.3s' }}
              >
                {/* Show sender name for messages not from current user */}
                {msg.senderId !== userEmail && (
                <div className="text-[10px] font-semibold text-gray-600 mb-1">
                  { msg.senderName && msg.senderName !== 'Anonymous' ? msg.senderName : msg.senderId}
                </div>
                )}
                <span>{msg.content}</span>
                <div className="text-[10px] text-right opacity-60 mt-1">
                {new Date(msg.timestamp).toLocaleDateString()} {new Date(msg.timestamp).toLocaleTimeString()}
                </div>
                {/* Optional: Add a small triangle for the bubble */}
                <span className={`absolute bottom-0 ${msg.senderId === userEmail ? 'right-0' : 'left-0'} w-0 h-0 border-t-8 border-t-transparent border-b-0 border-b-transparent ${msg.senderId === userEmail ? 'border-l-8 border-l-green-600' : 'border-r-8 border-r-white'}`}></span>
              </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
            {otherTyping && (
              <div className="text-xs text-gray-500 mt-2 animate-pulse">Someone is typing...</div>
            )}
          </div>
        </div>
        {socketRef.current && userEmail && <ChatInput
          input={input}
          setInput={setInput}
          handleInputChange={handleInputChange}
          handleSend={handleSend}
          disabled={loadingMessages}
        />}
      </div>
    </div>
  );
}

export default UserChat;

// ChatInput component
function ChatInput({ input, setInput, handleInputChange, handleSend, disabled }: {
  input: string;
  setInput: React.Dispatch<React.SetStateAction<string>>;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSend: () => void;
  disabled: boolean
}) {
  return (
    <div className="p-4 bg-white border-t flex items-center max-w-xl mx-auto w-full sticky bottom-0 z-10">
      <input
        className="flex-1 border rounded px-3 py-2 mr-2 focus:outline-none focus:ring"
        type="text"
        value={input}
        onChange={handleInputChange}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSend();
        }}
        placeholder="Type your message..."
      />
      <button
        className="bg-green-400  px-4 py-2 rounded disabled:opacity-50"
        onClick={handleSend}
        disabled={!input.trim() || disabled}
      >
        Send
      </button>
    </div>
  );
}

/*

TRUNCATE `sci_live_chat_2`.`_ReadBy`;
DELETE FROM `sci_live_chat_2`.`Message` where id <> '';
DELETE FROM `sci_live_chat_2`.`ChatMember` where id <> '';
DELETE FROM `sci_live_chat_2`.`Chat` where id <> '';


*/